import { ComboboxButton, ComboboxInput } from '@headlessui/react';
import { cn } from 'cn';
import Icon from '../../Icon';
import Tooltip from '../../Tooltip';
import type { Option } from '../InputCombobox';
import {
  type ComboboxMultiFieldProps,
  InputComboboxMultiField,
} from './ComboboxMultiField';

interface ComboboxFieldProps<T> {
  closeButtonRef: React.RefObject<HTMLButtonElement | null>;
  errorId: string;
  hasError: boolean;
  inputId: string;
  isOpen?: boolean;
  label: string;
  labelClassName?: string;
  labelDescription?: React.ReactNode;
  name: string;
  onQueryChange: (query: string) => void;
  options?: Option<T>[];
  placeholder?: string;
  multiField?: ComboboxMultiFieldProps<T>;
  setReference: (node: HTMLElement | null) => void;
  tooltip?: string;
}

export function InputComboboxField<T>({
  closeButtonRef,
  errorId,
  hasError,
  inputId,
  isOpen,
  label,
  labelClassName,
  labelDescription,
  name,
  onQueryChange,
  options,
  placeholder,
  multiField,
  setReference,
  tooltip,
}: ComboboxFieldProps<T>) {
  return (
    <>
      <Tooltip tooltip={tooltip} enabled={!!tooltip}>
        <div>
          <label
            htmlFor={inputId}
            className={cn('laane:mb-1 block', labelClassName)}
          >
            {label}
          </label>
          {labelDescription ? (
            <div
              className={cn(
                'laane:contents kunde:text-neutral-100 kunde:text-2xs',
              )}
            >
              {labelDescription}
            </div>
          ) : null}
        </div>
      </Tooltip>
      <div
        className={cn('group relative flex items-center', {
          'flex-wrap gap-2': !!multiField,
        })}
        ref={multiField ? setReference : undefined}
      >
        {multiField ? (
          <InputComboboxMultiField {...multiField} />
        ) : (
          <ComboboxInput
            onChange={(event) => onQueryChange(event.target.value)}
            className="group-hover:bg-neutral-10 w-full kunde:h-10"
            aria-describedby={hasError ? errorId : undefined}
            aria-invalid={hasError}
            displayValue={(value) =>
              options?.find((option) => option.value === value)?.label ?? ''
            }
            placeholder={placeholder}
            name={name}
            ref={setReference}
            id={inputId}
          />
        )}
        <ComboboxButton
          ref={closeButtonRef}
          className="absolute right-2 flex items-center border-none! bg-transparent!"
          aria-label="Toggle options"
        >
          <Icon
            type="chevron-down"
            className={cn('text-neutral-110 ', { 'rotate-180': isOpen })}
          />
        </ComboboxButton>
      </div>
    </>
  );
}
